<template>
  <div class="wt-asset-editor-stage-inner wt-to-snap">
    <!-- failed message -->
    <div
      class="wt-asset-preview-overlay-message wt-failed"
      v-show="currentPreviewStatus.failed"
    ></div>
    <!-- /failed message -->
    <!-- loading spinner -->
    <div class="wt-video-loading-shutter" v-show="shouldShowLoadingShutter">
      <div class="text">
        <div class="subtext">{{ progressText }}</div>
        <div class="progressbar">
          <div
            class="progress"
            :style="{ width: currentPreviewStatus.progress + '%' }"
          >
            <div class="pbaranim"></div>
            <div class="buffer-spinner"></div>
          </div>
        </div>
      </div>
    </div>
    <!-- /loading spinner -->

    <!-- object tracking in progress -->
    <div
      v-if="isTrackingAsset"
      class="wt-stage-tracking"
      :title="trackingTitle"
    >
      <wt-google-like-loader class="-default" />
      <span v-text="'Tracking objects...'" />
    </div>
    <!-- /object tracking in progress -->

    <!-- rtp player -->
    <div class="wt-rtp-display" ref="rtpDisplay">
      <div id="js-rcp-playbox-1" class="wt-rtp-player">
        <canvas for="player"></canvas>
        <canvas for="movie-processor"></canvas>
        <canvas for="diagnostic"></canvas>

        <!-- stage curtains :aspect-ratio="ASPECT_16_9" -->
        <div class="wt-editor-curtains">
          <div class="wt-to-snap"></div>
        </div>

        <!-- /stage curtains -->
        <img for="bug-logo" :src="logoBugImageURL" v-if="logoBugImageURL" />

        <div for="buffer-indicator">
          <img
            src="https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/preloader.svg"
          />
        </div>

        <img
          for="pause-indicator"
          alt="Play"
          src="/resources/img/play-icon-02.svg"
        />

        <img
          for="play-indicator"
          alt="Pause"
          src="/resources/img/pause-icon-02.svg"
        />

        <layout-overlay
          v-if="!inPreviewMode && !isEffectPage && !isEditingLayout"
          :sceneLayoutSTPModel="sceneLayoutSTPModel"
        />

        <!-- static preview -->
        <div class="wt-stp-overlay" v-if="shouldShowSTP">
          <div class="-stp-spinner wt2-sg" v-if="stp.status === 'PENDING'">
            <wt-google-like-loader class="-default" />
          </div>
          <img :src="stp.src" v-if="stp.src" />
        </div>
        <!-- /static preview -->

        <div for="mount-RcpControlPanel"></div>
      </div>
    </div>
    <!-- /rtp player -->
    <div v-if="showDuration" class="-duration">
      Duration: {{ displayDuration }}
    </div>

    <!-- logoBugImageURL -->
    <div class="wt-stage-bug-logo" v-show="logoBugImageURL && !inPreviewMode">
      <img :src="logoBugImageURL" />
    </div>
    <!-- /logoBugImageURL -->
  </div>
</template>

<script>
import app from 'app';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import RCP from '@wochitnpm/rcp';
import LayoutOverlay from './LayoutOverlay.vue';
import RtpPlayerController from 'c/RtpPlayerController';
import { getAssetRectOnStage } from 'AssetEditor/store/assetEditorApi.js';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { workPool } from '@wochitnpm/helpers';

export default {
  data: function () {
    return {
      Player: {},
      currentPreviewStatus: {}, // points to Status/prdStatus according to situation,
      previewExpected: false,
      showAssetContainerAs: null,
      rtpStatus: {
        active: false,
        progress: 0,
        failed: false,
      },
      prdStatus: {
        active: false,
        progress: 0,
        failed: false,
      },
      workPoolHandlers: new Set(),
      cropModal: null,
    };
  },

  props: {
    showDuration: {
      type: Boolean,
      default: true,
    },
  },

  components: {
    LayoutOverlay,
  },

  computed: {
    ...mapGetters('assetEditor', [
      'cssStyleObj',
      'currentPage',
      'workModel',
      'originalImageSrc',
      'originalVideoSrc',
      'AGD',
      'selectedOverlayAsset',
      'selectedLayout',
      'stageDimensions',
      'disableAspectRatioCurtains',
      'sceneLayoutCustomOverlay',
      'presets',
    ]),
    ...mapState('assetEditor', [
      'story',
      'stp',
      'rtpModel',
      'sceneLayoutSTPModel',
      'assetModel',
      'originalAsset',
      'playheadPosition',
      'themeInfo',
      'shouldPreviewAutoplay',
      'narrationAssets',
    ]),
    ...mapState('assetEditor', {
      tmpAGD: (state) => ({
        scale: state.tmpAGD.scale,
        offset: state.tmpAGD.offset,
      }),
    }),

    isEditingLayout() {
      return app.storyController.story
        .get('_layoutPresets')
        ?.includes(this.currentPage?.id);
    },

    isEffectPage() {
      return (
        this.currentPage?.layers && this.currentPage?.layers[0] === 'EFFECT'
      );
    },

    isTrackingAsset() {
      return app.objectTrackingController.isTrackingAsset(this.workModel.guid);
    },

    trackingTitle() {
      return 'Tracking objects, the preview will refresh when it is done';
    },

    displayDuration() {
      const d = this.workModel.displayDuration;
      return app.moment.utc(d * 1000).format('m:ss.S');
    },

    shouldShowSTP() {
      return (
        !this.inPreviewMode && this.stp.status && !this.shouldShowLoadingShutter
      );
    },

    shouldShowLoadingShutter() {
      return (
        this.currentPreviewStatus.active && !this.currentPreviewStatus.failed
      );
    },

    playbackRate() {
      return this.AGD.videoSpeed;
    },

    currentPageId: function () {
      return this.currentPage.id;
    },

    inPreviewMode: function () {
      return this.currentPage.previewMode;
    },

    shouldLoopRCP: function () {
      return this.currentPageId === 'audio';
    },

    shouldPlay: function () {
      return this.currentPageId !== 'zoom';
    },

    progressText: function () {
      return +this.currentPreviewStatus.progress === 100
        ? app.fragmentPreview.WAIT
        : app.fragmentPreview.CREATING;
    },

    originalAssetStyle: function () {
      return Object.assign(
        {
          position: 'absolute',
          display: this.originalAsset.loading ? 'none' : 'block',
        },
        this.originalAsset.style
      );
    },

    cropValues: function () {
      return {
        scale: this.currentPageId === 'zoom' ? null : this.workModel.agd.scale,
        offset:
          this.currentPageId === 'zoom' ? null : this.workModel.agd.offset,
      };
    },

    logoBugImageURL: function () {
      return app.prependHttp(this.themeInfo.logoBugImageURL);
    },

    volumeControl: function () {
      return this.workModel.volumeControl;
    },

    switchMainAndOverlay: function () {
      return this.selectedLayout.switchMainAndOverlay;
    },
  },

  mounted() {
    this.$_mountRTPController();
    this.originalAssetLoaded();
    this.updateStage();
    bus.$on('updateStage', this.updateStage);
    bus.$on('refreshOverlayRCP', this.showOverlayAssetRCP);
    bus.$on('volumeChange', this.updateVolume);

    if (this.rtpModel && !this.rtpModel.isDone()) {
      this.watchRTPModel();
    }

    this.watchPRD();

    if (
      this.inPreviewMode &&
      (this.assetModel.isIMAGE() ||
        this.assetModel.isSOCIALIMAGE() ||
        this.assetModel.isWEBPAGE())
    ) {
      this.preloadOrignalImage();
    }
    if (this.currentPageId !== 'preview') {
      if (this.currentPageId === 'overlay_asset') {
        this.showOverlayAssetRCP();
      } else {
        this.setStaticUrl();
      }
    }
    this.$root.$on('pause-video', () => {
      this.rtpPlayer?.pause();
    });
  },

  beforeDestroy: function () {
    bus.$off('updateStage', this.updateStage);
    bus.$off('refreshOverlayRCP', this.showOverlayAssetRCP);
    bus.$off('volumeChange', this.updateVolume);
    this.$root.$off('pause-video');
    this.rtpPlayer.destroy();
    if (this.rtpModel) {
      this.rtpModel.off(
        'change:realTimeFetchProgress change:parts change:status',
        this.updateRTPStatus
      );
      this.rtpModel.off('change:parts', this.rtpModelHasData);
      this.rtpModel.off(
        'change:realTimePreviewResultData',
        this.rtpModelHasData
      );
    }
    this.assetModel.off(
      'change:previewResultData change:_previewStep change:_previewOp',
      this.prdModelChanged
    );
    clearTimeout(this.$options.rtpListenerTimeout);

    workPool.off(this.workPoolHandlers);
  },

  watch: {
    /* Triggered once new rtpModel assigned or set to Null */
    rtpModel: function () {
      this.watchRTPModel();
    },
    playheadPosition: function () {
      /* listen to TOT timeline playhead */
      this.rtpPlayer.rcp.seek(
        this.playheadPosition * this.rtpPlayer.displayDuration
      );
    },
    previewExpected: function () {
      switch (this.previewExpected) {
        case 'rtp':
          this.currentPreviewStatus = this.rtpStatus;
          break;
        case 'prd':
          this.currentPreviewStatus = this.prdStatus;
          break;
        default:
          this.currentPreviewStatus = {};
          break;
      }
    },
    inPreviewMode: function () {
      this.rtpPlayer.reset();
      this.updateStage({
        play: false,
      });
    },
    cropValues: function () {
      // crop values have changed
      // set original asset style to empty for re-calculation on next effect entrance
      this.setOriginalAsset({
        style: {},
      });
    },
    shouldLoopRCP: function (shouldLoop) {
      this.rtpPlayer.toggleLoop(shouldLoop);
    },

    'workModel.displayDuration': _.debounce(
      function () {
        if (this.rtpPlayer) {
          this.rtpPlayer.setDisplayDuration(this.workModel.displayDuration);
          this.updateStage();
        }
        bus.$emit('apply-click');
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: true }
    ),
    selectedLayout: function () {
      if (this.currentPageId === 'overlay_asset') {
        this.showOverlayAssetRCP();
      }
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setShouldPreviewAutoplay',
      'setOriginalAsset',
      'setTimeInAsset',
      'setTimeInAssetToFirstTextOverlay',
    ]),

    showOverlayAssetRCP() {
      const asset = _.cloneDeepAndClean(this.workModel);
      asset.overlayTracks = [this.selectedOverlayAsset];
      this.rtpPlayer.setStaticUrlOverlayAsset({
        asset,
        ignoreRtp: true,
        play: false,
      });
    },

    updateVolume({ volumeControl, onVoPlayVolumeControl }) {
      this.rtpPlayer.reflectVolumeChange({
        volumeControl,
        onVoPlayVolumeControl,
      });
    },

    $_mountRTPController: function () {
      this.rtpPlayer = new RtpPlayerController({
        $box: $(this.$refs.rtpDisplay),
        narrationAssets: this.narrationAssets,
        bgMusic: this.story.bgMusic,
        volumeControl: this.workModel.volumeControl,
        onVoPlayVolumeControl: this.workModel.onVoPlayVolumeControl,
        displayStartTime: this.workModel.displayStartTime,
        displayDuration: this.workModel.displayDuration,
        displayEndTime: this.workModel.displayEndTime,
        assetUUID: this.workModel.metadata.assetUUID,
        assetType: this.workModel.type,
        loop: this.shouldLoopRCP,
        canPlay: this.shouldPlay,
        commonVolume: this.currentPageId !== 'audio',
        removeCurtains: !!this.disableAspectRatioCurtains,
      });
      // Ugly hack alert!! We need to wait until TextOverlayTimeline is mounted before we can start listening.
      // NextTick does not work.
      this.$options.rtpListenerTimeout = setTimeout(() => {
        this.rtpPlayer.rcp.on(RCP.MSG.PROGRESS, (time) => {
          this.setTimeInAsset(
            app.sfp(time / this.workModel.displayDuration, 1e4)
          );
        });
      }, 500);
    },

    /*  The main stage was build to show only one 'task' at a time.
          That can be either rtp, loader (previewExpected), prd or static image
          updateStage should be called only when the current task is outdated, and another task should be shown:
          switching effects, prd/rtp arrival, rtp in progress etc.
          updateStage holds the logics for what to show in the stage, a
          according to the current effect reflected by asset data.
          It uses the following flags to automatically re-generate its HTML and show the updated layers:
          previewExpected, showAssetContainerAs
      */
    updateStage: function (/* options */) {
      if (this._isDestroyed) {
        return;
      }

      // options = Object.assign({}, options);

      this.previewExpected = false;
      this.showAssetContainerAs = null;

      if (this.inPreviewMode && !_.isEmpty(this.rtpModel)) {
        // rtp available, user in preview mode
        if (this.rtpModel.hasParts()) {
          this.rtpPlayer.marshalPreviewParts(
            this.rtpModel,
            !!this.shouldPreviewAutoplay
          );
          if (!this.rtpModel.isDone()) {
            this.previewExpected = 'rtp'; // we are expecting more parts to come
          }
        } else if (this.rtpModel.isDone()) {
          this.setStaticUrl({
            asset: Object.assign({}, this.workModel, this.rtpModel.attributes),
            ignoreRtp: false,
            play: !!this.shouldPreviewAutoplay,
          });
        } else {
          this.previewExpected = 'rtp'; // we are expecting rtp done state
        }
      } else {
        // no rtp
        if (this.assetModel.isPrdInProgress()) {
          this.previewExpected = 'prd'; // we are expecting prd on assetmodel
        } else {
          if (this.inPreviewMode) {
            if (this.assetModel.hasVideoContent()) {
              // display previewResultData video
              this.rtpPlayer.setDisplayDuration(this.workModel.displayDuration);
            }
            this.setStaticUrl();
          } else {
            this.setOriginalAsset();
            if (this.assetModel.hasVideoContent()) {
              this.$nextTick(() => {
                this.setTimeInAssetToFirstTextOverlay();
              });
            } else if (app.assetType.isWALLPAPER(this.workModel)) {
              this.showAssetContainerAs = 'wallpaper';
            } else {
              this.showAssetContainerAs = 'image';
            }
          }
        }
      }
    },

    watchPRD: function () {
      const assetId = this.assetModel.id;
      this.updatePRDStatus();
      workPool.on(
        {
          type: workPool.job.cropSocialAsset.type,
          context: (jc) => jc.assetId === assetId,
        },
        () => {
          this.prdModelChanged();
        },
        this.workPoolHandlers
      );

      this.assetModel.on(
        'change:previewResultData change:_previewStep change:_previewOp',
        this.prdModelChanged
      );
    },

    prdModelChanged: function () {
      this.updatePRDStatus();
      if (this.assetModel.get('previewResultData')) {
        if (this.previewExpected === 'prd') {
          this.updateStage();
        }
      }
    },

    updatePRDStatus: function () {
      const progress = (this.assetModel.get('_previewStep') || 0) * 20;
      this.prdStatus.active = this.assetModel.isPrdInProgress();
      this.prdStatus.progress = progress > 100 ? 100 : progress;
      this.prdStatus.failed = this.assetModel.isPrdFailed();
    },

    watchRTPModel: function () {
      this.rtpPlayer.reset();
      this.updateStage();
      this.updateRTPStatus();

      if (!this.rtpModel) {
        // rtpModel removed. updateStage's no-rtp branch paints the original
        // asset, which is not what the overlay asset page shows - repaint the
        // collage composite the same way mounted() does, or the stage goes blank
        if (this.currentPageId === 'overlay_asset') {
          this.showOverlayAssetRCP();
        }
        return;
      }

      const rtpEvent = this.rtpModel.isRequestingParts()
        ? 'change:parts'
        : 'change:realTimePreviewResultData';

      this.rtpModel.on(rtpEvent, this.rtpModelHasData);
      this.rtpModel.on(
        'change:realTimeFetchProgress change:parts change:status',
        this.updateRTPStatus
      );
    },

    rtpModelHasData: function () {
      if (this.previewExpected === 'rtp') {
        this.updateStage();
      }
    },

    updateRTPStatus: function () {
      this.rtpStatus.active =
        this.rtpModel && !(this.rtpModel.hasParts() || this.rtpModel.isDone());
      this.rtpStatus.progress =
        this.rtpModel && this.rtpModel.get('realTimeFetchProgress');
      this.rtpStatus.failed = this.rtpModel && this.rtpModel.isFailed();
    },

    /*
     * Warning below:
     * Social assets are unique because they contain an rtp as prd
     * please keep the social conditionals available, 10x
     */
    originalAssetLoaded: function () {
      const rect = getAssetRectOnStage({
        metadata: this.workModel.metadata,
        scale: this.cropValues.scale,
        offset: this.cropValues.offset,
        stageDimensions: this.stageDimensions,
        isSocial:
          this.assetModel.isSOCIAL() || this.assetModel.isSOCIALFRAGMENT(),
        isWebpage: this.assetModel.isWEBPAGE(),
      });

      if (!rect) {
        return; // dimensions unknown - leave the previous style in place
      }

      this.setOriginalAsset({
        style: {
          width: rect.width + 'px',
          height: rect.height + 'px',
          top: rect.top + 'px',
          left: rect.left + 'px',
        },
      });
    },

    preloadOrignalImage: function () {
      let i = document.createElement('img');
      i.onload = function () {
        i = null;
      };
      i.src = this.originalImageSrc;
    },

    setStaticUrl: function ({ ignoreRtp = true, play = false, asset } = {}) {
      if (this.currentPageId === 'crop') {
        return;
      }
      asset = asset || this.workModel;
      /**
       * if the first layer in the layers array is `"EFFECT"`.
       * it's mean that this custom overlay (CO) is base on the asset and not on the canvas.
       *
       * this means that the overlay asset(s) layout damages the placing of the CO.
       *
       * `ignoreOverlay` true, if the CO is an "asset base" layout or the page is `'zoom'`.
       * so that when placing the CO it will show only the asset that will affected,
       * the main asset (a.k.a. background asset)
       */
      asset = _.cloneDeep(asset);
      const ignoreOverlay =
        this.currentPageId === 'zoom' ||
        (this.currentPage.layers && this.currentPage.layers[0] === 'EFFECT');
      const ignoreCropping = this.currentPageId === 'zoom';
      if (
        !ignoreOverlay &&
        !ignoreCropping &&
        (this.sceneLayoutCustomOverlay ||
          (this.currentPage?.isSceneLayout && this.currentPage?.cropRectangle))
      ) {
        const layoutPreset = this.presets.find((p) => {
          return this.currentPage?.isSceneLayout &&
            this.currentPage?.cropRectangle
            ? p.id === this.currentPage.id
            : p.id === this.sceneLayoutCustomOverlay?.id;
        });
        // console.log(layoutPreset);
        if (!layoutPreset) {
          throw new Error(
            `Layout preset not found in theme info: ${this.sceneLayoutCustomOverlay.id}`
          );
        }
        const wallpaper = {
          type: 'wallpapermetaasset',
          metadata: {},
          wallpaperMetaData: {
            type: 'SOLID',
            wallpaperData: {
              colorValue: 'rgba(0,0,0,1)',
            },
          },
          agd: {},
          displayStartTime: asset.displayStartTime,
          displayDuration: asset.displayDuration,
          displayEndTime: asset.displayEndTime,
        };
        asset.overlayTracks = [
          {
            ...wallpaper,
            sceneLayout: {
              overlayAssetBoundingBox: {
                top: 0,
                bottom: 1,
                left: 0,
                right: 1,
                zIndex: 1,
              },
              mainAssetBoundingBox: {
                ...layoutPreset.cropRectangle,
                zIndex: 2,
              },
              switchMainAndOverlay: false,
            },
          },
        ];
        this.rtpPlayer.setStaticUrlOverlayAsset({
          asset,
          ignoreRtp,
          play,
        });
      } else {
        this.rtpPlayer.setStaticUrl({
          asset,
          ignoreRtp,
          play,
          ignoreOverlay,
          ignoreCropping,
          fitCanvasToImage:
            this.currentPageId === 'zoom' && app.assetType.isWEBPAGE(asset),
        });
      }
    },
  },
};
</script>
